type LocationTier = 'lcol' | 'mcol' | 'hcol'
type ChildcareType = 'none' | 'family' | 'daycare' | 'nanny'
type InsuranceType = 'employer' | 'marketplace' | 'medicaid'

interface BabyCostInput {
  locationTier: LocationTier
  insuranceType: InsuranceType
  childcareType: ChildcareType
  existingKids: number
  householdIncome: number
  filingStatus: string
}

// Monthly cost estimates by tier
const COST_TABLES: Record<LocationTier, Record<string, number>> = {
  lcol: {
    diapers: 70,
    formula: 150,
    clothing: 50,
    gear: 80,
    medical: 100,
    food_solids: 60,
    daycare: 800,
    nanny: 1800,
    family: 200,
  },
  mcol: {
    diapers: 85,
    formula: 175,
    clothing: 70,
    gear: 100,
    medical: 150,
    food_solids: 80,
    daycare: 1200,
    nanny: 2500,
    family: 300,
  },
  hcol: {
    diapers: 100,
    formula: 200,
    clothing: 100,
    gear: 130,
    medical: 200,
    food_solids: 100,
    daycare: 2000,
    nanny: 3500,
    family: 400,
  },
}

const INSURANCE_COSTS: Record<InsuranceType, Record<LocationTier, number>> = {
  employer: { lcol: 200, mcol: 300, hcol: 400 },
  marketplace: { lcol: 300, mcol: 450, hcol: 600 },
  medicaid: { lcol: 0, mcol: 0, hcol: 0 },
}

export function calculateBabyCost(input: BabyCostInput) {
  const costs = COST_TABLES[input.locationTier]

  // Monthly breakdown
  const childcareMonthly = input.childcareType === 'none' ? 0
    : input.childcareType === 'daycare' ? costs.daycare
    : input.childcareType === 'nanny' ? costs.nanny
    : costs.family

  const insuranceAdditional = INSURANCE_COSTS[input.insuranceType][input.locationTier]

  const monthlyBreakdown = [
    { category: 'Childcare', amount: childcareMonthly },
    { category: 'Diapers & Wipes', amount: costs.diapers },
    { category: 'Formula/Feeding', amount: costs.formula },
    { category: 'Clothing', amount: costs.clothing },
    { category: 'Gear & Supplies', amount: costs.gear },
    { category: 'Medical/Copays', amount: costs.medical },
    { category: 'Insurance (additional)', amount: insuranceAdditional },
    { category: 'Baby Food (6mo+)', amount: Math.round(costs.food_solids / 2) }, // average over first year
  ]

  const totalMonthly = monthlyBreakdown.reduce((s, b) => s + b.amount, 0)
  const firstYearTotal = totalMonthly * 12

  // One-time costs (nursery setup, car seat, stroller, etc)
  const oneTimeCosts = input.locationTier === 'hcol' ? 5000
    : input.locationTier === 'mcol' ? 3500 : 2500

  const firstYearWithSetup = firstYearTotal + oneTimeCosts

  // Child Tax Credit — $2,000 per child, phases out at $200k single / $400k MFJ
  const CTC_PER_CHILD = 2000
  const CTC_PHASE_OUT_SINGLE = 200000
  const CTC_PHASE_OUT_MFJ = 400000
  const phaseOutThreshold = input.filingStatus === 'mfj' ? CTC_PHASE_OUT_MFJ : CTC_PHASE_OUT_SINGLE

  // Phase-out: credit reduces by $50 per $1,000 over threshold
  const excessIncome = Math.max(0, input.householdIncome - phaseOutThreshold)
  const phaseOutReduction = Math.floor(excessIncome / 1000) * 50

  const totalChildren = input.existingKids + 1
  const existingKids = input.existingKids

  // Full credits before phase-out
  const totalCreditFull = totalChildren * CTC_PER_CHILD
  const existingCreditFull = existingKids * CTC_PER_CHILD

  // Credits after phase-out (floor at $0)
  const totalCreditAfterPhaseOut = Math.max(0, totalCreditFull - phaseOutReduction)
  const existingCreditAfterPhaseOut = Math.max(0, existingCreditFull - phaseOutReduction)

  // New credit from baby = difference between total and existing (what the baby adds)
  const newBabyCredit = totalCreditAfterPhaseOut - existingCreditAfterPhaseOut
  const monthlyTaxBenefit = Math.round(newBabyCredit / 12)

  const isPhaseOut = phaseOutReduction > 0

  const netFirstYear = firstYearWithSetup - newBabyCredit

  // 5-year projection with 3% inflation
  const inflationRate = 0.03
  const fiveYearProjection = []
  let cumulativeCost = 0
  for (let year = 1; year <= 5; year++) {
    const inflationMultiplier = Math.pow(1 + inflationRate, year - 1)
    // Childcare costs often increase; diapers go away around year 3
    const diapersMultiplier = year <= 3 ? 1 : 0
    const yearlyBase = (
      (childcareMonthly + costs.formula + costs.clothing + costs.gear + costs.medical + insuranceAdditional + costs.food_solids) * inflationMultiplier * 12 +
      costs.diapers * diapersMultiplier * inflationMultiplier * 12
    )
    const yearCost = year === 1 ? yearlyBase + oneTimeCosts : yearlyBase
    cumulativeCost += yearCost
    fiveYearProjection.push({
      year,
      annualCost: Math.round(yearCost),
      cumulativeCost: Math.round(cumulativeCost),
      taxCredit: totalCreditAfterPhaseOut,
      netCost: Math.round(yearCost - totalCreditAfterPhaseOut),
    })
  }

  // Percentage of income
  const percentOfIncome = input.householdIncome > 0
    ? (firstYearTotal * 12 / input.householdIncome) * 100 / 12 // monthly as % of monthly income
    : 0

  return {
    monthlyBreakdown,
    totalMonthly: Math.round(totalMonthly),
    firstYearTotal: Math.round(firstYearTotal),
    oneTimeCosts,
    firstYearWithSetup: Math.round(firstYearWithSetup),
    childTaxCredit: newBabyCredit,
    totalFamilyCredit: totalCreditAfterPhaseOut,
    existingCredit: existingCreditAfterPhaseOut,
    totalChildren,
    isPhaseOut,
    monthlyTaxBenefit,
    netFirstYear: Math.round(netFirstYear),
    fiveYearProjection,
    percentOfIncome: Math.round(percentOfIncome * 10) / 10,
    locationTier: input.locationTier,
    childcareType: input.childcareType,
  }
}
